/**
 * RTF — read-only rich text viewing (§8, §16).
 *
 * The catalog rates RTF `convert-edit`: it can be edited only by converting to
 * another format, because writing RTF back would lose the control words this
 * reader does not model. Converting is a capability this build does not have
 * yet, so the honest thing (§19) is to open RTF for reading and offer no Edit
 * button — which is exactly what a handler with no `exportBytes` produces, since
 * the UI derives the affordance from its absence.
 *
 * The model is a {@link DocxModel}, so an RTF lands in the same document viewer
 * as a Word file, with the same search, outline and plain-text export. What it
 * carries is deliberately modest: paragraphs, runs, bold/italic/underline and
 * alignment. §19 forbids dressing a partial rendering up as a faithful one, so
 * the file-information screen names the code page and flags any embedded object
 * or picture that was present but not shown.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import type { DocxModel } from '../docx/handler.js';
import { parseRtf } from './rtf.js';

export type { RtfDocument } from './rtf.js';
export { parseRtf, rtfToPlainText } from './rtf.js';

const PREVIEW_PARAGRAPHS = 40;

/**
 * RTF is a byte stream, not UTF-8 text. It must be decoded as Latin-1 so that
 * every byte maps to one character: `\'hh` escapes, `\binN` byte counts and
 * group braces all depend on byte positions the parser walks by index.
 */
function decodeLatin1(bytes: Uint8Array): string {
  return new TextDecoder('windows-1252', { fatal: false }).decode(bytes);
}

function looksLikeRtf(text: string): boolean {
  return text.slice(0, 20).replace(/^﻿/, '').startsWith('{\\rtf');
}

export const rtfHandler: FormatHandler<DocxModel> = {
  id: 'rtf',
  formatIds: ['rtf'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const text = decodeLatin1(source.bytes);
    if (!looksLikeRtf(text)) {
      throw new HandlerError('error.damagedOrIncomplete', 'not an RTF document (no {\\rtf header)');
    }
    const doc = parseRtf(text, PREVIEW_PARAGRAPHS);

    return {
      kind: 'pages',
      unitCount: doc.truncated ? undefined : doc.paragraphs.length,
      unitKey: 'viewer.paragraph',
      firstContent: {
        blocks: doc.blocks.slice(0, PREVIEW_PARAGRAPHS),
        more: doc.truncated,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<DocxModel>> {
    throwIfAborted(ctx.signal);
    const text = decodeLatin1(source.bytes);
    if (!looksLikeRtf(text)) {
      throw new HandlerError('error.damagedOrIncomplete', 'not an RTF document (no {\\rtf header)');
    }

    const maxParagraphs = Math.max(1, Math.floor(ctx.limits.maxSpreadsheetCells / 8));
    const doc = parseRtf(text, maxParagraphs);
    throwIfAborted(ctx.signal);
    if (doc.paragraphs.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'the document has no readable content');
    }

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    // §19: say what this reader did not render rather than let it pass as full.
    warnings.push({
      messageKey: 'status.limitedSupport',
      severity: 'info',
      detail: 'RTF is shown as rich text — paragraphs, bold, italic, underline and alignment. Tables, pictures and other formatting are not rendered. Editing an RTF requires converting it to another format.',
    });
    if (doc.hasEmbeddedObjects) {
      warnings.push({
        messageKey: 'security.embeddedObject',
        severity: 'caution',
        detail: 'This document embeds an OLE object (\\objdata), which can conceal an executable. Its data is never decoded or run (§30).',
      });
    }
    if (doc.hasPictures) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'info',
        detail: 'The document contains pictures, which this reader does not display.',
      });
    }
    if (doc.truncated) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'degraded',
        detail: `the document exceeds the paragraph budget for this device; the remainder is not shown`,
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large document (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        blocks: doc.blocks,
        paragraphs: doc.paragraphs,
        edits: new Map(),
        images: new Map(),
        hasMacros: false,
        hasTrackedChanges: false,
        hasComments: false,
        hasHeadersOrFooters: false,
        hasFootnotes: false,
        wordCount: doc.wordCount,
      },
      // No exportBytes: RTF is read-only in this build, so it opens read-only
      // whatever the session would otherwise allow.
      readOnly: true,
      editLevel: spec?.edit ?? 'convert-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  // No exportBytes and no validateExport: this format is not edited in place.
};
